import React, { useEffect, useState } from "react";
import {
  deleteBlog,
  updateBlogStatus,
  paginationBlogs,
} from "../../services/blogsServices";
import TableHeader from "../Common/TableComponent/TableHeader";
import TablesRow from "../Common/TableComponent/TablesRow";
import TableToolbar from "../Common/TableComponent/TableToolbar";
import { useNavigate } from "react-router-dom";
import ToggleSwitch from "./ToggleSwitch";
import ToggleSwitchFeatured from "./ToggleSwitchFeatured";
import Swal from "sweetalert2";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { Loading } from "../Common/OtherElements/Loading";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../utils/errorHandler";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { Pagination } from "../Common/TableComponent/Pagination";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";

export const ManageBlogs = () => {
  const navigate = useNavigate();
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [manageBlogs, setManageBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const PageLevelAccessurl = "blogs";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      const formData = {
        pageSize: entriesPerPage,
        pageNo: currentPage,
        fromDate,
        toDate,
        fiterParam: searchQuery,
      };
      try {
        const response = await paginationBlogs(formData);
        const { data } = response;
        setManageBlogs(data.result);
        setTotalCount(data.result[0]?.totalCount || 0);
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [entriesPerPage, currentPage, fromDate, toDate, searchQuery]);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete("Blog");
    if (confirmed) {
      try {
        await deleteBlog(id);
        setManageBlogs((prev) => prev.filter((item) => item.id !== id));
        Swal.fire(
          "Deleted!",
          "The blog has been deleted successfully.",
          "success",
        );
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateBlogStatus(id, newStatus);
      setManageBlogs((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, status: newStatus } : item,
        ),
      );
    } catch (error) {
      handleErrors(error);
    }
  };

  const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

  return (
    <>
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-list-card">
              <div className="card-header">
                <div>
                  <h5>Manage Blogs</h5>
                  <span className="xc-list-count">
                    {totalCount} {totalCount === 1 ? "blog" : "blogs"}
                  </span>
                </div>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  entriesOptions={[30, 50, 100, 200]}
                  searchValue={searchQuery}
                  searchPlaceholder="Search blogs..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  left={
                    <>
                      <div className="xc-blog-date-filter">
                        <Flatpickr
                          id="fromDate"
                          className="form-control"
                          placeholder="From date"
                          value={fromDate}
                          onChange={([date]) => {
                            setFromDate(date);
                            setCurrentPage(1);
                          }}
                          options={{
                            dateFormat: "Y-m-d",
                            monthSelectorType: "static",
                          }}
                        />
                      </div>
                      <div className="xc-blog-date-filter">
                        <Flatpickr
                          id="toDate"
                          className="form-control"
                          placeholder="To date"
                          value={toDate}
                          onChange={([date]) => {
                            setToDate(date);
                            setCurrentPage(1);
                          }}
                          options={{
                            dateFormat: "Y-m-d",
                            monthSelectorType: "static",
                          }}
                        />
                      </div>
                    </>
                  }
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Blog Name",
                          "Blog Category",
                          "Posted By",
                          "Tags",
                          "Posted On",
                          "Status",
                          "Published?",
                          "Featured?",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {manageBlogs.length === 0 ? (
                          <TableDataStatusError colspan="10" />
                        ) : (
                          manageBlogs.map((item, index) => (
                            <TablesRow
                              key={item.id}
                              rowData={{
                                BlogId:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                BlogName: (
                                  <span className="xc-cell-title">
                                    {item.blogName}
                                  </span>
                                ),
                                BlogCategory: (
                                  <span className="xc-pill">
                                    {item.blogCategory || "—"}
                                  </span>
                                ),
                                PostedBy: item.postedBy,
                                BlogTags: item.blogTags,
                                PostedOn: new Date(
                                  item.postedOn,
                                ).toLocaleDateString(),
                                status: (
                                  <span
                                    className={`badge badge-soft-${
                                      item.status === "Active"
                                        ? "success"
                                        : "danger"
                                    }`}
                                  >
                                    {item.status === "Active"
                                      ? "Active"
                                      : "Draft"}
                                  </span>
                                ),
                                publish: (
                                  <ToggleSwitch
                                    blogId={item.id}
                                    initialStatus={item.status}
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(item.id, newStatus)
                                    }
                                  />
                                ),
                                featured: (
                                  <ToggleSwitchFeatured
                                    flatId={item.id}
                                    initialStatus={item.featured}
                                  />
                                ),
                              }}
                              columns={[
                                "BlogId",
                                "BlogName",
                                "BlogCategory",
                                "PostedBy",
                                "BlogTags",
                                "PostedOn",
                                "status",
                                "publish",
                                "featured",
                              ]}
                              hideIcons={false}
                              onEdit={() => {
                                navigate(`update/${item.id}`);
                              }}
                              onDelete={() => handleDelete(item.id)}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalEntries={totalCount}
                  entriesPerPage={entriesPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
